import { type GetRef } from 'antd'
import clsx from 'clsx'
import {
  parseISO,
  set,
  getHours,
  getMinutes,
  getDate,
  getYear,
  getMonth
} from 'date-fns'
import {
  forwardRef,
  useCallback,
  useMemo,
  type ComponentPropsWithoutRef,
  type FC
} from 'react'

import { DatePicker } from '../DatePicker'
import { RemoveIconButton } from '../RemoveIconButton'

const { TimePicker } = DatePicker

type Props = Omit<ComponentPropsWithoutRef<'div'>, 'onChange'> & {
  /** 時間を設定（ISO形式） */
  value?: string | null
  /** リセットボタンの表示可否 */
  allowReset?: boolean
  /** 値変更時のコールバック */
  onChange?: (date: string) => void
  /** リセットボタンクリックのコールバック */
  onReset?: () => void
  /** DatePickerのpropsを指定 */
  datePickerProps?: Omit<
    ComponentPropsWithoutRef<typeof DatePicker<Date>>,
    'value' | 'onChange' | 'allowClear'
  >
  /** TimePickerのpropsを指定 */
  timePickerProps?: Omit<
    ComponentPropsWithoutRef<typeof TimePicker<Date>>,
    'value' | 'onChange' | 'format' | 'allowClear'
  >
  dateValueOverride?: Date | null
  timeValueOverride?: Date | null
}

/**
 * 日時選択のコンポーネント（DatePickerとTimePickerの組み合わせ）
 * - DatePickerとTimePickerの値を連動させる
 */
export const DateTimePicker: FC<Props> = forwardRef<
  GetRef<typeof DatePicker>,
  Props
>(
  (
    {
      value,
      className,
      allowReset,
      datePickerProps,
      timePickerProps,
      onChange,
      onReset,
      dateValueOverride,
      timeValueOverride,
      ...props
    },
    ref
  ) => {
    const value_ = useMemo(() => (value ? parseISO(value) : null), [value])

    const baseDateForCombine =
      dateValueOverride !== undefined ? dateValueOverride : value_
    const baseTimeForCombine =
      timeValueOverride !== undefined ? timeValueOverride : value_

    const dateInputValue =
      dateValueOverride !== undefined ? dateValueOverride : value_
    const timeInputValue =
      timeValueOverride !== undefined ? timeValueOverride : value_

    const handleChangeDate = useCallback(
      (picked: Date) => {
        if (!onChange) return
        // picked (y/m/d) を、時刻の基準 (override or value_) に合成
        const base =
          baseTimeForCombine ??
          // 基準がない場合は 00:00 に固定して日付を作る
          new Date(
            picked.getFullYear(),
            picked.getMonth(),
            picked.getDate(),
            0,
            0,
            0,
            0
          )

        const combined = set(base, {
          year: getYear(picked),
          month: getMonth(picked),
          date: getDate(picked)
        })
        onChange(combined.toISOString())
      },
      [onChange, baseTimeForCombine]
    )

    const handleChangeTime = useCallback(
      (picked: Date) => {
        if (!onChange) return
        // picked (h/m) を、日付の基準 (override or value_) に合成
        const base =
          baseDateForCombine ??
          // 基準がない場合は「今日」を使う（必要なら 1970-01-01 に固定でもOK）
          new Date()

        const combined = set(base, {
          hours: getHours(picked),
          minutes: getMinutes(picked),
          seconds: 0,
          milliseconds: 0
        })
        onChange(combined.toISOString())
      },
      [onChange, baseDateForCombine]
    )

    return (
      <div {...props} className={clsx(['flex items-center gap-1', className])}>
        <DatePicker
          ref={ref}
          showNow={false}
          allowClear={false}
          format="yyyy-MM-dd"
          {...datePickerProps}
          value={dateInputValue}
          onChange={handleChangeDate}
        />
        <TimePicker
          showNow={false}
          allowClear={false}
          needConfirm={false}
          {...timePickerProps}
          format="HH:mm"
          value={timeInputValue}
          onChange={handleChangeTime}
        />
        {allowReset && <RemoveIconButton onClick={onReset} />}
      </div>
    )
  }
)
